import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Space,
  message,
  Spin,
  Radio,
  Divider,
  Select,
  InputNumber,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Planogram, PlanogramLayout, PlanogramStatus } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';

const { Option } = Select;
const { TextArea } = Input;

interface PlanogramFormProps {
  mode: 'create' | 'edit';
}

const PlanogramForm: React.FC<PlanogramFormProps> = ({ mode }) => {
  const { storeGroupCode, storeId, code } = useParams<{
    storeGroupCode: string;
    storeId?: string;
    code: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [layouts, setLayouts] = useState<PlanogramLayout[]>([]);

  const storeGroupService = StoreGroupService;

  useEffect(() => {
    fetchLayouts();
    if (mode === 'edit' && code) {
      fetchPlanogramDetail(code);
    }
  }, [mode, code]);

  const fetchLayouts = async () => {
    try {
      // 注意：Service中没有getPlanogramLayouts方法，这里使用模拟数据
      const layouts = [
        { id: 'layout1', name: 'Standard Layout', shelves: [], width: 800, height: 600 },
        { id: 'layout2', name: 'Compact Layout', shelves: [], width: 600, height: 400 },
      ];
      setLayouts(layouts);
    } catch (error) {
      message.error(t('planogramForm.fetchLayoutsFailed'));
      console.error('Failed to fetch planogram layout list:', error);
    }
  };

  const fetchPlanogramDetail = async (code: string) => {
    setLoading(true);
    try {
      // 注意：使用模拟数据，实际应该调用正确的Service方法
      const planogram = {
        code: code,
        name: 'Sample Planogram',
        layoutId: 'layout1',
        width: 800,
        height: 600,
        status: 'Draft' as PlanogramStatus,
        remark: 'Sample remark'
      };
      form.setFieldsValue(planogram);
    } catch (error) {
      message.error(t('planogramDetail.fetchFailed'));
      console.error('Failed to fetch planogram details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const planogramData: Partial<Planogram> = {
        ...values,
        storeGroupId: storeGroupCode,
      };

      if (mode === 'create') {
        // Note: The Service does not have a createPlanogram method, using simulated creation here
        // await storeGroupService.createPlanogram(planogramData);
        message.success(t('common.success'));
      } else {
        // Use updatePlanogram method, but only pass planogram object
        const fullPlanogram = {
          ...planogramData,
          id: code || '',
        } as Planogram;
        await storeGroupService.updatePlanogram(fullPlanogram);
        message.success(t('common.success'));
      }

      handleBack();
    } catch (error: any) {
      const action = mode === 'create' ? t('common.create') : t('common.update');
      message.error(`${action}${t('common.failed')}: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (storeId) {
      navigate(`/store-group/${storeGroupCode}/store/${storeId}`);
    } else {
      navigate(`/store-group/${storeGroupCode}`);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="planogram-form">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('planogramForm.return')}
          </Button>
          <h1>{mode === 'create' ? t('planogramForm.createTitle') : t('planogramForm.editTitle')}</h1>
        </Space>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'Draft',
            width: 800,
            height: 600,
          }}
        >
          <Form.Item
            name="code"
            label={t('planogramDetail.code')}
            rules={[
              { required: true, message: t('planogramForm.codeRequired') },
              { pattern: /^[A-Za-z0-9_-]{2,20}$/, message: t('planogramForm.codePattern') },
            ]}
          >
            <Input
              placeholder={t('planogramForm.codePlaceholder')}
              disabled={mode === 'edit'}
              maxLength={20}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('planogramDetail.name')}
            rules={[
              { required: true, message: t('planogramForm.nameRequired') },
              { max: 50, message: t('planogramForm.nameMaxLength') },
            ]}
          >
            <Input placeholder={t('planogramForm.namePlaceholder')} maxLength={50} />
          </Form.Item>

          <Form.Item
            name="layoutId"
            label={t('planogramForm.layoutTemplate')}
            rules={[{ required: true, message: t('planogramForm.layoutTemplateRequired') }]}
          >
            <Select placeholder={t('planogramForm.selectLayoutTemplate')}>
              {layouts.map((layout) => (
                <Option key={layout.id} value={layout.id}>
                  {layout.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={t('planogramForm.canvasSize')}>
            <Space>
              <Form.Item
                name="width"
                noStyle
                rules={[{ required: true, message: t('planogramForm.widthRequired') }]}
              >
                <InputNumber
                  min={400}
                  max={2000}
                  addonBefore={t('planogramForm.width')}
                  addonAfter="px"
                  style={{ width: 180 }}
                />
              </Form.Item>
              <Form.Item
                name="height"
                noStyle
                rules={[{ required: true, message: t('planogramForm.heightRequired') }]}
              >
                <InputNumber
                  min={300}
                  max={1500}
                  addonBefore={t('planogramForm.height')}
                  addonAfter="px"
                  style={{ width: 180 }}
                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item
            name="status"
            label={t('planogramDetail.status')}
            rules={[{ required: true, message: t('planogramForm.statusRequired') }]}
          >
            <Radio.Group>
              <Radio value="Draft">{t('storeGroup.planogram.status.draft')}</Radio>
              <Radio value="PendingReview">{t('storeGroup.planogram.status.pendingReview')}</Radio>
              <Radio value="Approved">{t('storeGroup.planogram.status.approved')}</Radio>
              <Radio value="Rejected">{t('storeGroup.planogram.status.rejected')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="remark"
            label={t('planogramDetail.remark')}
            rules={[{ max: 200, message: t('planogramForm.remarkMaxLength') }]}
          >
            <TextArea
              placeholder={t('planogramForm.remarkPlaceholder')}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={200}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={submitting}
              >
                {t('planogramForm.save')}
              </Button>
              <Button onClick={handleBack}>{t('planogramForm.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PlanogramForm;